import React, { useState } from 'react';
import {
  Col,
  Container,
  Modal,
  ModalBody,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import ModalHeader from '../ModalHeader';
import {
  addOrRemoveYearsToGivenDate,
  DateFormats,
  formatDate,
} from '../../utils/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../Button';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';

export type PaymentScheduleModalProps = React.PropsWithoutRef<{
  showModal: boolean;
  handleClose: () => void;
}>;

type PaymentSchedule = {
  paymentDueDate: string;
  deadlineForAutoPayUpdate: string;
  autoPayDeductionDate: string;
};

const today = new Date();

const currentYear = today.getFullYear();

const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1);
};
const getLastDayOfMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0);
};

const getLastWorkingDaysOfTheMonth = (year: number, month: number) => {
  const date = getLastDayOfMonth(year, month); //last day of the month
  const dayOfWeek = getLastDayOfMonth(year, month).getDay();
  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
    return date;
  }

  return new Date(year, month, 5);
};

const addOrRemoveDaysToDate = (date: Date, daysToAdd: number) => {
  return new Date(new Date(date).setDate(new Date(date).getDate() + daysToAdd));
};
const getFirstWorkingDaysOfTheMonth = (year: number, month: number) => {
  const date = getFirstDayOfMonth(year, month); //first day of the month

  const dayOfWeek = date.getDay(); //last day of the month
  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
    return addOrRemoveDaysToDate(date, dayOfWeek == 5 ? 3 : 1);
  }

  if (dayOfWeek === 0) {
    const d = addOrRemoveDaysToDate(date, 1);

    return d;
  }
  if (dayOfWeek === 6) {
    const d2 = addOrRemoveDaysToDate(date, 2);

    return d2;
  }
  return date;
};

const generatePaymentSchedule = (): PaymentSchedule[] => {
  const schedule: PaymentSchedule[] = [];

  for (let i = 0; i < 12; i++) {
    const paymentDueDate: string =
      formatDate(
        getFirstDayOfMonth(currentYear, i),
        DateFormats.SHORT_MONTH_DAY_YEAR
      ) || '';

    const lastYear = addOrRemoveYearsToGivenDate(today, -1).getFullYear();
    const deadlineForAutoPayUpdate: string =
      formatDate(
        getLastWorkingDaysOfTheMonth(
          i == 0 ? lastYear : currentYear,
          i == 0 ? 12 : i
        ),
        DateFormats.DAY_MONTH_DATE_Year
      ) || '';

    const autoPayDeductionDate: string =
      formatDate(
        getFirstWorkingDaysOfTheMonth(currentYear, i),
        DateFormats.DAY_MONTH_DATE_Year
      ) || '';

    schedule.push({
      paymentDueDate,
      deadlineForAutoPayUpdate,
      autoPayDeductionDate,
    });
  }
  return schedule;
};

const PaymentScheduleModal = ({
  showModal = false,
  handleClose,
  ...props
}: PaymentScheduleModalProps) => {
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule[]>(
    generatePaymentSchedule()
  );

  return (
    <Modal show={showModal} onHide={handleClose} {...props} size="xl">
      <ModalHeader closeButton className="pb-0">
        <h5>Payment Schedule</h5>
      </ModalHeader>
      <ModalBody className="pt-0 ">
        <Container>
          <Row>
            <Col className={'col-12 mb-10 mt-10 p-0'}>
              <p className="text-fs-6 lh-xs text-dark-gray-50 fw-light">
                Listed below are your due dates for any changes or start of your
                automated payments and when they will go into effect. If you
                have already submitted payment by check, you should wait until
                after that statement's payment due date before starting
                automated payments.
              </p>
            </Col>
          </Row>

          <Row>
            <Col className={'col-12 overflow-auto'}>
              <Row
                className={'overflow-hidden'}
                style={{
                  minWidth: '650px',
                }}
              >
                <Col>
                  <Row className="text-fs-6 fw-light pt-3 lh-xs border-bottom px-3 pb-6 text-black ">
                    <Col className="text-start fw-normal pe-0 col-lg-4 col-4 ">
                      Payment Due Date
                    </Col>
                    <Col className="text-start fw-normal pe-0 col-lg-4 col-4 ">
                      Deadline for Auto-Pay update
                      <OverlayTrigger
                        placement={'top'}
                        overlay={
                          <Tooltip id={`tooltip-top`}>
                            Deadline to change or set up Auto-Pay to meet
                            payement due date.
                          </Tooltip>
                        }
                      >
                        <span className={'lh-xs ps-2'}>
                          <FontAwesomeIcon icon={light('info-circle')} />
                        </span>
                      </OverlayTrigger>
                    </Col>
                    <Col className="text-start fw-normal pe-0 col-4 ">
                      Auto-Pay Deduction Date
                    </Col>
                  </Row>

                  {paymentSchedule.map((schedule, index) => {
                    return (
                      <Row
                        className="text-fs-6 fw-light lh-xs pt-3 pb-3 px-3 border-bottom"
                        key={index}
                      >
                        <Col className=" pe-0 col-4 text-start  pt-2">
                          {schedule.paymentDueDate}
                        </Col>
                        <Col className=" pe-0 col-4 text-start  pt-2">
                          {schedule.deadlineForAutoPayUpdate}
                        </Col>
                        <Col className="pe-0 col-4 text-start  pt-2">
                          {schedule.autoPayDeductionDate}
                        </Col>
                      </Row>
                    );
                  })}
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className="justify-content-center mt-10 mb-10">
            <Col className={'col-3 col-lg-2'}>
              <Button
                variant="primary-outline"
                className={'w-100'}
                onClick={handleClose}
              >
                Close
              </Button>
            </Col>
          </Row>
        </Container>
      </ModalBody>
    </Modal>
  );
};

export default PaymentScheduleModal;
